import React, { useCallback, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import useFetchActionbarAuth from 'src/hooks/useFetchActionbarAuth';
import { useLoadingSpinnerStore } from 'src/store/util/loadingSpinnerUtil';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { showSuccess, ShowSuccessFetch, showWarning } from 'src/utils/toast/toastUtil';
import { validateString } from 'src/utils/string/stringUtil';
import { InsertRowHandler } from 'src/utils/grid/GridHandlerContent';
import { useCommonCodeValidate } from 'src/utils/commonCode/CommonCodeValidateContext';
import {
	isNumericInspectionValueType,
	isValidInspectionValue,
} from 'src/utils/inspection/inspectionValueUtils';
import { downloadSettingExcel } from 'src/utils/blob/blobUtil';
import {
	buildCommonCodeRequirements,
	TQC5010_R01_ROW_VALIDATE,
} from 'src/utils/commonCode/commonCodeRequirementConfig';
import {
	deleteTQC5010_R01, downloadExcel_TQC5010_R01, getTQC5010_R01AllCount, getTQC5010_R01List,
	patchTQC5010_R01, postTQC5010_R01,
} from './Api_TQC5010_R01';
import { defaultTQC5010_R01Dto, TQC5010_R01Dto } from './Dto_TQC5010_R01';
import {  TQC5010_R01Filter, useTQC5010_R01Store } from './Store_TQC5010_R01';

interface Props {
	gridRef: React.RefObject<AgGridReact<TQC5010_R01Dto> | null>;
	setMessage: (message: string) => void;
}

type FetchMessageMode = 'show' | 'none';

interface FetchOptions {
	messageMode?: FetchMessageMode;
}

export const Content_TQC5010_R01 = ({ setMessage }: Props) => {
	const {
		TQC5010_R01Filter,
		TQC5010_R01PastFilter, setTQC5010_R01PastFilter,
		TQC5010_R01List, setTQC5010_R01List, insertTQC5010_R01List,
		deletedTQC5010_R01List, addDeletedTQC5010_R01List, removeDeletedTQC5010_R01List,
		selectedTQC5010_R01List, setSelectedTQC5010_R01List, removeSelectedTQC5010_R01List,
		setOriginalTQC5010_R01List,
		setAllCount,
		setScrollTop,
	} = useTQC5010_R01Store();

	const { inspectionValueList } = useCommonCodeStore();
	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { validateCommonCodeLists } = useCommonCodeValidate();
	const [clickable, setClickable] = useState(true);

	useFetchActionbarAuth('TQC5010_R01');

	const getParams = (filter: TQC5010_R01Filter): Partial<TQC5010_R01Filter> => ({
		inspItemGroup: filter.inspItemGroup || undefined,
		inspItemNm: filter.inspItemNm || undefined,
	});

	const isSameFilter = (a: TQC5010_R01Filter, b: TQC5010_R01Filter) =>
		a.inspItemGroup === b.inspItemGroup && a.inspItemNm === b.inspItemNm;

	const renewalAllCount = useCallback(async () => {
		const allCountResponse = await getTQC5010_R01AllCount(getParams(TQC5010_R01Filter));
		setAllCount(allCountResponse.data.data);
		return allCountResponse.data.data;
	}, [TQC5010_R01Filter, setAllCount]);

	/**
	 * 등록·수정 대상 행의 필수값·숫자형·복합 PK 중복을 검증한다.
	 * PK = (inspItemGroup, inspType, inspItemCd) 3개 조합.
	 */
	const validateInsertData = useCallback((data: TQC5010_R01Dto, currentRowIndex: number) => {
		if (!validateString(data.inspItemGroup)) {
			showWarning('검사항목그룹을 입력해주세요.');
			return false;
		}
		if (!validateString(data.inspType)) {
			showWarning('검사구분을 선택해주세요.');
			return false;
		}
		if (!validateString(data.inspItemNm)) {
			showWarning('검사기준명을 입력해주세요.');
			return false;
		}
		if (!validateString(data.valueType)) {
			showWarning('검사값유형을 선택해주세요.');
			return false;
		}
		if (!validateString(data.inspMethod)) {
			showWarning('검사방법을 선택해주세요.');
			return false;
		}

		if (isNumericInspectionValueType(data.valueType)) {
			if (validateString(data.specValue1)
				&& !isValidInspectionValue(data.valueType, data.specValue1.trim())) {
				showWarning('검사값1을 숫자로 입력해주세요.');
				return false;
			}
			if (validateString(data.specValue2)
				&& !isValidInspectionValue(data.valueType, data.specValue2.trim())) {
				showWarning('검사값2를 숫자로 입력해주세요.');
				return false;
			}
		}

		/* 복합 PK(inspItemGroup + inspType + inspItemCd) 중복 검증 */
		const activeRows = TQC5010_R01List.filter(
			(row) => !deletedTQC5010_R01List.includes(row.valueIndex.toString()),
		);
		const otherRows = activeRows.filter((_, index) => index !== currentRowIndex);

		if (validateString(data.inspItemCd) && otherRows.some(
			(row) => row.inspItemGroup === data.inspItemGroup
				&& row.inspType === data.inspType
				&& row.inspItemCd === data.inspItemCd,
		)) {
			showWarning(
				`검사항목그룹, 검사구분, 검사기준코드가 중복됩니다. (${data.inspItemGroup} / ${data.inspType} / ${data.inspItemCd})`,
			);
			return false;
		}
		return true;
	}, [TQC5010_R01List, deletedTQC5010_R01List]);

	const executeDataFetch = useCallback(async (
		fetchType: 'search' | 'refetch',
		options?: FetchOptions,
	) => {
		const messageMode = options?.messageMode ?? 'show';
		setLoadingSpinner(true);

		const same = isSameFilter(TQC5010_R01Filter, TQC5010_R01PastFilter);

		if (!same || fetchType === 'refetch') {
			setScrollTop(null);
		}

		if (fetchType === 'refetch' || !same) {
			setTQC5010_R01List([]);
		}

		const responseData = await getTQC5010_R01List(
			'search',
			getParams(TQC5010_R01Filter),
			TQC5010_R01List.length ?? 0,
		);

		const newList = [...responseData.data.data] as TQC5010_R01Dto[];
		setTQC5010_R01List(newList);

		const processAllCount = await renewalAllCount();
		setOriginalTQC5010_R01List(newList);
		setSelectedTQC5010_R01List([]);
		removeDeletedTQC5010_R01List(deletedTQC5010_R01List);

		if (newList.length === 0) {
			if (messageMode === 'show') {
				showWarning('검사항목이 존재하지 않습니다.');
			}
		} else {
			setTQC5010_R01PastFilter(TQC5010_R01Filter);
			if (messageMode === 'show') {
				ShowSuccessFetch(setMessage, `검사항목 조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
			}
		}

		setLoadingSpinner(false);
		if (messageMode === 'show') {
			setTimeout(() => setMessage(''), 5000);
		}
	}, [
		TQC5010_R01Filter, TQC5010_R01PastFilter, TQC5010_R01List, deletedTQC5010_R01List,
		setMessage, setLoadingSpinner,
		setOriginalTQC5010_R01List, setSelectedTQC5010_R01List, setTQC5010_R01List,
		removeDeletedTQC5010_R01List, renewalAllCount, setTQC5010_R01PastFilter, setScrollTop,
	]);

	const fetchData = useCallback(async () => {
		await executeDataFetch('search');
	}, [executeDataFetch]);

	const handleExcelClick = useCallback(async () => {
		try {
			setLoadingSpinner(true);
			const response = await downloadExcel_TQC5010_R01(getParams(TQC5010_R01Filter));
			if (!response.data.data) {
				showWarning('엑셀로 내보낼 검사항목이 없습니다.');
				return;
			}
			downloadSettingExcel({
				responseData: response.data.data,
				fileName: '검사항목 등록.xlsx',
			});
		} catch (error) {
			console.error('검사항목 엑셀 다운로드 실패:', error);
			showWarning('검사항목 엑셀 다운로드 중 오류가 발생했습니다.');
		} finally {
			setLoadingSpinner(false);
		}
	}, [TQC5010_R01Filter, setLoadingSpinner]);

	/** 신규 행 삽입 시 사용할 기본 행 데이터를 생성한다. (검사값유형 기본값 = inspectionValueList 1번) */
	const defaultTQC5010_R01Row = useCallback(() => {
		const commonResult = validateCommonCodeLists(
			buildCommonCodeRequirements(TQC5010_R01_ROW_VALIDATE, {
				inspectionValueList,
			}),
		);
		if (!commonResult.valid) {
			return { ...defaultTQC5010_R01Dto };
		}

		return {
			...defaultTQC5010_R01Dto,
			valueType: inspectionValueList[0].minorCd,
			inserted: true,
		};
	}, [inspectionValueList, validateCommonCodeLists]);

	const handleInsertClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
		InsertRowHandler({
			event: e,
			dataList: TQC5010_R01List,
			rowData: { ...defaultTQC5010_R01Row(), valueIndex: -1 },
			insertRow: insertTQC5010_R01List,
			defaultRowData: defaultTQC5010_R01Row(),
			setClickable,
		});
	}, [TQC5010_R01List, insertTQC5010_R01List, defaultTQC5010_R01Row, setClickable]);

	const handleSaveClick = useCallback(async () => {
		const insertedList = TQC5010_R01List.filter(
			(row) => !deletedTQC5010_R01List.includes(row.valueIndex.toString()) && row.inserted === true,
		);
		const modifiedList = TQC5010_R01List.filter(
			(row) => !deletedTQC5010_R01List.includes(row.valueIndex.toString()) && row.isModified && !row.inserted,
		);

		let valid = true;
		modifiedList.forEach((value) => {
			const index = TQC5010_R01List.findIndex((row) => row.valueIndex === value.valueIndex);
			if (!validateInsertData(value, index)) valid = false;
		});
		if (!valid) return;

		insertedList.forEach((value) => {
			const index = TQC5010_R01List.findIndex((row) => row.valueIndex === value.valueIndex);
			if (!validateInsertData(value, index)) valid = false;
		});
		if (!valid) return;

		const deleteStringList = new Set(deletedTQC5010_R01List.map((item) => item.toString()));
        const deleteTargetList = TQC5010_R01List.filter(
            (item) => deleteStringList.has(item.valueIndex.toString()),
		);

		const [insertedResponse, modifiedResponse, deleteResponse] = await Promise.all([
			insertedList.length > 0 ? postTQC5010_R01(insertedList) : Promise.resolve(undefined),
			modifiedList.length > 0 ? patchTQC5010_R01(modifiedList) : Promise.resolve(undefined),
			deletedTQC5010_R01List.length > 0
				? Promise.all(deleteTargetList.map((value) => deleteTQC5010_R01(value)))
				: Promise.resolve(undefined),
		]);

		if (insertedResponse?.data.successFlag || modifiedResponse?.data.successFlag) {
			showSuccess('검사항목 등록이 완료되었습니다.');
			setMessage('검사항목 등록이 완료되었습니다.');
		} else if (deleteResponse) {
			showSuccess('검사항목 삭제가 완료되었습니다.');
			setMessage('검사항목 삭제가 완료되었습니다.');
		}

		setTimeout(() => setMessage(''), 5000);
		await executeDataFetch('refetch', { messageMode: 'none' });
	}, [
		validateInsertData, TQC5010_R01List, deletedTQC5010_R01List, executeDataFetch, setMessage,
	]);

	const handleDeleteClick = useCallback(() => {
		if (deletedTQC5010_R01List.length > 0) {
			removeDeletedTQC5010_R01List(deletedTQC5010_R01List);
			removeSelectedTQC5010_R01List();
			setTQC5010_R01List([...TQC5010_R01List]);
		} else if (selectedTQC5010_R01List.length > 0) {
			addDeletedTQC5010_R01List(selectedTQC5010_R01List);
			removeSelectedTQC5010_R01List(selectedTQC5010_R01List);
		}
	}, [
		selectedTQC5010_R01List, deletedTQC5010_R01List,
		addDeletedTQC5010_R01List, removeDeletedTQC5010_R01List,
		removeSelectedTQC5010_R01List, setTQC5010_R01List, TQC5010_R01List,
	]);

	return {
		clickable, setClickable,
		fetchData,
		handleExcelClick,
		defaultTQC5010_R01Row,
		handleInsertClick,
		handleSaveClick,
		handleDeleteClick,
	};
};
